import React, { FC } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import IndicativeNumber from '../../../../../../../../gqlCommon/components/indicativeNumber/IndicativeNumber';
import useSelectedCurrencyPrice from '../../../../../../../../gqlCommon/hooks/currency/useSelectedCurrencyPrice';
import useScrollToSection from '../../../../../../../hooks/navigation/useScrollToSection';
import SummaryLabelTitle from '../../../summaryLabel/SummaryLabelTitle';
import PriceValue from '../priceValue/PriceValue';
import Translation from '../../../../../../../../gqlCommon/components/translation/Translation';
import summaryLabelStyles from '../../../summaryLabel/SummaryLabelStyles';
import { PriceDisplayType } from '../../../../../../../../../../common/enum/PriceDisplayType';
import clsx from 'clsx';
import { GetCustomizeCar_customizeCar_prices_totalPrice } from '../../../../../../../graphql/queries/__generated__/GetCustomizeCar';
import useCustomizeCarContext from '../../../../../../../hooks/useCustomizeCarContext';
import { PriceSources } from '../../../../../../../../common/enum/PriceSources';

export interface ITotalPriceProps {
  prices: GetCustomizeCar_customizeCar_prices_totalPrice[];
  currency: string;
  loading?: boolean;
  isComplexModel?: boolean;
  taxLabel?: string;
  oneForOneReplacement?: boolean;
}

const SummaryTotalPrice: FC<ITotalPriceProps> = ({
  currency,
  prices,
  loading,
  isComplexModel,
  taxLabel,
  oneForOneReplacement,
}) => {
  const { scrollToDisclaimer } = useScrollToSection();
  const styles = summaryLabelStyles();
  const { selectedCar } = useCustomizeCarContext();
  const { selectedCurrencyPrice } = useSelectedCurrencyPrice(prices);

  const hasIndicativePrice = selectedCar?.car?.attributes?.hasIndicativePrice;
  const isDealerPrice = selectedCar?.prices.source === PriceSources.DEALER;

  const totalPriceTranslation = isDealerPrice
    ? 'trans__configurator__customise_page_summary_section__dealer_listed_driveaway_price_label'
    : hasIndicativePrice
    ? 'trans__configurator__customise_page_summary_section__estimated_price_label'
    : 'trans__configurator__customise_page_summary_section__driveaway_price_label';

  return (
    <Grid
      container
      justifyContent="space-between"
      spacing={3}
      className={clsx(styles.lineContainer, styles.driveAwayPrice)}
    >
      <SummaryLabelTitle
        label={
          <Box
            data-test="customise:summary:vehicle_total_price"
            component="span"
            className={clsx(styles.label, styles.payableLabel)}
          >
            <Translation id={totalPriceTranslation} />
            <IndicativeNumber
              dataTest="vehicle_total_price"
              className={clsx(styles.boldLabel, styles.indicativeText)}
              displayNumber={
                <Translation
                  id={
                    hasIndicativePrice
                      ? 'trans__configurator__indicative_numbers__estimatedPrice'
                      : 'trans__configurator__indicative_numbers__vehicleTotalPrice'
                  }
                />
              }
              onClick={scrollToDisclaimer}
            />
            {oneForOneReplacement && (
              <Typography component={'span'} variant="body1" data-test={'text:finance-price:replacement-label'}>
                <Translation
                  className={styles.replacementLabel}
                  id={'trans__configurator__customise_page_summary_section__one_for_one_label'}
                />
              </Typography>
            )}
          </Box>
        }
        boldLabel={isComplexModel}
        colSm={8}
        colXs={6}
      />
      <PriceValue
        loading={loading}
        price={selectedCurrencyPrice}
        priceType={PriceDisplayType.PRIMARY}
        currency={currency}
        boldPrice
        testId="vehicle_total_price"
        taxLabel={taxLabel}
        className={styles.vehicleTotalPrice}
      />
    </Grid>
  );
};

export default SummaryTotalPrice;
